<script lang="ts" setup>
import { onMounted } from 'vue'

const route = useRoute()

const { podcastId, episodeId } = route.params as { podcastId: string; episodeId: string }

const { data: podcast } = await usePodcast(podcastId)

if (!podcast.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

const { data: episode } = await useEpisode(podcast.value.identifier, episodeId)

if (!episode.value) {
  throw showError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

if (podcast.value.identifier !== podcastId || episode.value.identifier !== episodeId) {
  navigateTo(episode.value.route)
}

useSeo({
  title: `${episode.value.title} - Listen Now on ${podcast.value.title}`,
  description: `Listen to ${episode.value.title} from ${podcast.value.title}. Stay updated with the latest episodes and catch up on all your favorite shows today!`,
  image: episode.value.metaLogo,
})

onMounted(() => {
  if (podcast.value) {
    if (podcast.value.external) {
      window.gtag('event', 'page_view', {
        content_group: 'Third Party Podcasts',
      })
    } else {
      window.gtag('event', 'page_view', {
        content_group: 'Zeno Podcasts',
      })
    }
  }
})
</script>

<template>
  <div v-if="episode && podcast">
    <div class="flex md:flex-row flex-col md:space-x-8 space-x-0 md:space-y-0 space-y-8 md:mb-16 mb-8">
      <div class="flex md:justify-start justify-center">
        <EpisodeLogo :alt="episode.title" :src="episode.logo" />
      </div>
      <div class="flex flex-row items-center md:justify-start justify-center">
        <div class="space-y-5">
          <div class="md:text-left text-center">
            <EpisodeMetadata
              :duration="episode.duration"
              :explicit="episode.explicit"
              :published-date="episode.publishedDate"
              is-title
            />

            <h1 class="md:text-2xl text-lg font-semibold mb-2">
              {{ episode.title }}
            </h1>

            <h2>
              <NuxtLink :to="podcast.route" class="block hover:underline md:text-lg">
                {{ podcast.title }}
              </NuxtLink>
            </h2>
          </div>

          <div class="flex space-x-3 md:justify-start justify-center">
            <EpisodePlaybackButton :episode="episode" :podcast="podcast" is-base />

            <NuxtLink
              v-if="episode.link"
              :to="episode.link"
              aria-label="Episode website"
              class="flex items-center justify-center rounded-full border-0 size-12 bg-zeno-gray-light hover:bg-gray-200 active:bg-gray-300"
              size="icon"
              target="_blank"
              variant="outline"
            >
              <Icon name="solar:square-share-line-linear" size="24" />
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <div v-if="episode.description" class="md:space-y-8 space-y-6">
      <div class="md:space-y-8 space-y-6">
        <div class="border-b border-zeno-gray-border flex space-x-4 md:justify-start justify-between">
          <div class="px-6 pb-4 text-lg border-b border-zeno-text flex items-center">About</div>
        </div>
      </div>

      <DescriptionText v-if="episode.description" :text="episode.description" />
    </div>
  </div>
</template>

<style scoped></style>
